import {
  ArrowRight,
  BarChart3,
  BrainCircuit,
  CheckCircle2,
  Layers,
  Lightbulb,
  MessageSquare,
  PenTool,
  Rocket,
  Target,
  Zap,
} from "lucide-react";
import React from "react";
import { cn } from "../../utils/cn";
import { Badge, Card } from "../ui";

interface ICPAnalysisDisplayProps {
  analysis: {
    content_analysis: {
      content_type: string;
      target_audience: string[];
      business_vertical: string;
      tone: string;
      complexity_level: string;
      engagement_potential: number;
    };
    icp_insights: {
      recommended_icp: {
        name: string;
        description: string;
        characteristics: string[];
        content_preferences: string[];
        engagement_style: string;
      };
      confidence_score: number;
      business_vertical: string;
      target_audience: string[];
      content_analysis: {
        tone: string;
        complexity: string;
        engagement_potential: number;
      };
    };
    recommendations: string[];
  };
}

const ICPAnalysisDisplay: React.FC<ICPAnalysisDisplayProps> = ({
  analysis,
}) => {
  const getScoreColor = (score: number) => {
    if (score >= 0.8) return "text-emerald-500";
    if (score >= 0.6) return "text-amber-500";
    return "text-rose-500";
  };

  return (
    <div className="space-y-8">
      {/* Content Analysis Grid */}
      <Card
        variant="default"
        className="p-8 rounded-[2.5rem] border-purple-200 shadow-xl shadow-gray-200/20 bg-white"
      >
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 rounded-2xl bg-indigo-50 text-indigo-600">
            <BarChart3 className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-xl font-black text-gray-900 tracking-tight">
              Análise Automática de Conteúdo
            </h3>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mt-1">
              Processamento de Linguagem Natural por IA
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="p-5 rounded-2xl bg-slate-50 border border-purple-200 group hover:border-purple-200 transition-all">
            <div className="flex items-center gap-2 mb-3">
              <Layers className="w-4 h-4 text-gray-400" />
              <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                Tipo
              </h4>
            </div>
            <p className="text-sm font-bold text-gray-700 capitalize group-hover:text-indigo-600">
              {analysis.content_analysis.content_type}
            </p>
          </div>

          <div className="p-5 rounded-2xl bg-slate-50 border border-purple-200 group hover:border-purple-200 transition-all">
            <div className="flex items-center gap-2 mb-3">
              <Target className="w-4 h-4 text-gray-400" />
              <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                Vertical
              </h4>
            </div>
            <p className="text-sm font-bold text-gray-700 capitalize group-hover:text-purple-600">
              {analysis.content_analysis.business_vertical}
            </p>
          </div>

          <div className="p-5 rounded-2xl bg-slate-50 border border-purple-200 group hover:border-blue-200 transition-all">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare className="w-4 h-4 text-gray-400" />
              <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                Tom de Voz
              </h4>
            </div>
            <p className="text-sm font-bold text-gray-700 capitalize group-hover:text-blue-600">
              {analysis.content_analysis.tone}
            </p>
          </div>

          <div className="p-5 rounded-2xl bg-slate-50 border border-purple-200 group hover:border-amber-200 transition-all">
            <div className="flex items-center gap-2 mb-3">
              <BrainCircuit className="w-4 h-4 text-gray-400" />
              <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                Complexidade
              </h4>
            </div>
            <p className="text-sm font-bold text-gray-700 capitalize group-hover:text-amber-600">
              {analysis.content_analysis.complexity_level}
            </p>
          </div>

          <div className="p-5 rounded-2xl bg-slate-50 border border-purple-200 group hover:border-emerald-200 transition-all">
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                Engajamento
              </h4>
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            </div>
            <p
              className={cn(
                "text-xl font-black",
                getScoreColor(analysis.content_analysis.engagement_potential),
              )}
            >
              {Math.round(analysis.content_analysis.engagement_potential * 100)}
              %
            </p>
          </div>

          <div className="p-5 rounded-2xl bg-slate-50 border border-purple-200">
            <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">
              Público-Alvo
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {analysis.content_analysis.target_audience.map(
                (audience, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="bg-white text-[9px] font-black text-gray-500 border-purple-200 uppercase tracking-tighter"
                  >
                    {audience}
                  </Badge>
                ),
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Recommended ICP - Premium Focus */}
      <Card
        variant="default"
        className="p-8 rounded-[3rem] border-purple-200 shadow-2xl bg-linear-to-br from-indigo-900 via-indigo-950 to-slate-900 text-white relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 p-12 opacity-5">
          <Target className="w-48 h-48" />
        </div>

        <div className="relative z-10">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-10 border-b border-white/10 pb-8">
            <div>
              <Badge
                variant="outline"
                className="text-indigo-300 border-purple-500/30 font-black text-[9px] uppercase tracking-[0.2em] mb-4"
              >
                ICP Recomendado pela IA
              </Badge>
              <h4 className="text-3xl font-black tracking-tight mb-2">
                {analysis.icp_insights.recommended_icp.name}
              </h4>
              <p className="text-indigo-200/70 text-sm font-medium max-w-xl leading-relaxed">
                {analysis.icp_insights.recommended_icp.description}
              </p>
            </div>
            <div className="flex flex-col items-center justify-center p-6 bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 shadow-inner group">
              <span
                className={cn(
                  "text-4xl font-black leading-none group-hover:scale-110 transition-transform",
                  getScoreColor(analysis.icp_insights.confidence_score),
                )}
              >
                {Math.round(analysis.icp_insights.confidence_score * 100)}%
              </span>
              <span className="text-[10px] font-black text-indigo-300 uppercase tracking-widest mt-2">
                Grau de Confiança
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="space-y-6">
              <h5 className="text-[10px] font-black text-indigo-300 uppercase tracking-widest flex items-center gap-2">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Perfil Psicográfico
              </h5>
              <div className="grid grid-cols-1 gap-3">
                {analysis.icp_insights.recommended_icp.characteristics.map(
                  (char, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 bg-white/5 rounded-xl border border-white/5 hover:bg-white/10 transition-colors"
                    >
                      <ArrowRight className="w-3 h-3 text-indigo-400" />
                      <span className="text-xs font-bold text-indigo-50">
                        {char}
                      </span>
                    </div>
                  ),
                )}
              </div>
            </div>

            <div className="space-y-6">
              <h5 className="text-[10px] font-black text-indigo-300 uppercase tracking-widest flex items-center gap-2">
                <PenTool className="w-3.5 h-3.5" />
                Preferências de Conteúdo
              </h5>
              <div className="flex flex-wrap gap-2">
                {analysis.icp_insights.recommended_icp.content_preferences.map(
                  (pref, index) => (
                    <Badge
                      key={index}
                      className="bg-indigo-500/20 text-indigo-100 border-purple-400/30 text-[10px] font-black px-3 py-1.5 uppercase"
                    >
                      {pref}
                    </Badge>
                  ),
                )}
              </div>
              <div className="mt-8 p-5 bg-linear-to-r from-purple-600 to-indigo-600 rounded-2xl shadow-lg border border-white/10">
                <p className="text-[9px] font-black text-purple-100 uppercase tracking-widest mb-1 opacity-70">
                  Estilo de Engajamento
                </p>
                <p className="text-xs font-black text-white italic">
                  "{analysis.icp_insights.recommended_icp.engagement_style}"
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Strategic Recommendations - Grid Cards */}
      <div className="space-y-6">
        <h3 className="text-xl font-black text-gray-900 tracking-tight flex items-center gap-3">
          <Lightbulb className="w-6 h-6 text-amber-500" />
          Protocolos de Atuação & Melhoria
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {analysis.recommendations.map((recommendation, index) => (
            <div
              key={index}
              className="flex items-start gap-4 p-6 bg-white rounded-3xl border border-purple-200 shadow-xs hover:shadow-md transition-all group"
            >
              <div className="w-10 h-10 rounded-2xl bg-amber-50 text-amber-500 flex items-center justify-center shrink-0 group-hover:bg-amber-500 group-hover:text-white transition-all">
                <Zap className="w-5 h-5" />
              </div>
              <p className="text-sm font-bold text-gray-700 leading-relaxed pt-1.5">
                {recommendation}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Strengths & Weaknesses - Modern Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="p-8 bg-emerald-50/50 rounded-[2.5rem] border border-emerald-100">
          <h4 className="font-black text-[10px] text-emerald-600 uppercase tracking-widest mb-6 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Vantagens Competitivas
          </h4>
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-white/80 rounded-2xl border border-emerald-100/50">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span className="text-xs font-bold text-gray-700">
                Tom de voz perfeitamente alinhado
              </span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-white/80 rounded-2xl border border-emerald-100/50">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span className="text-xs font-bold text-gray-700">
                Recorte vertical de alta precisão
              </span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-white/80 rounded-2xl border border-emerald-100/50">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span className="text-xs font-bold text-gray-700">
                Potencial de retenção orgânica elevado
              </span>
            </div>
          </div>
        </div>

        <div className="p-8 bg-indigo-50/50 rounded-[2.5rem] border border-purple-200">
          <h4 className="font-black text-[10px] text-indigo-600 uppercase tracking-widest mb-6 flex items-center gap-2">
            <Rocket className="w-4 h-4" />
            Eixos de Otimização
          </h4>
          <div className="space-y-4">
            {analysis.icp_insights.confidence_score < 0.8 && (
              <div className="flex items-center gap-3 p-3 bg-white/80 rounded-2xl border border-purple-200/50">
                <ArrowRight className="w-3.5 h-3.5 text-indigo-400" />
                <span className="text-xs font-bold text-gray-700">
                  Aprofundar densidade semântica
                </span>
              </div>
            )}
            <div className="flex items-center gap-3 p-3 bg-white/80 rounded-2xl border border-purple-200/50">
              <ArrowRight className="w-3.5 h-3.5 text-indigo-400" />
              <span className="text-xs font-bold text-gray-700">
                Implementar triggers de interação direta
              </span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-white/80 rounded-2xl border border-purple-200/50">
              <ArrowRight className="w-3.5 h-3.5 text-indigo-400" />
              <span className="text-xs font-bold text-gray-700">
                Expandir ecossistema de formatos
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ICPAnalysisDisplay;
